/**
 * Helpers puros da Projeção de Cartões (matriz compra parcelada × competência).
 * Livres de React para permitir testes unitários (vitest).
 *
 * A projeção vem completa do backend (todos os cartões, todas as empresas);
 * aqui vive a única lógica do cliente: filtrar e somar conforme a visão —
 * por cartão (tela de Cartões) ou por empresa (relatório de Despesas).
 */
import type {
  CreditCardProjection,
  CreditCardProjectionGroup,
} from '@/types/financial';

export type ProjectionView =
  | { kind: 'card'; cardId: number | null } // null = consolidado (todos os cartões)
  | { kind: 'company'; companyId: number | null }; // null = todas as empresas

/** O grupo pertence à visão? */
const groupInView = (g: CreditCardProjectionGroup, view: ProjectionView): boolean => {
  if (view.kind === 'card') return view.cardId === null || g.idCreditCards === view.cardId;
  return view.companyId === null || g.companyId === view.companyId;
};

/** Linhas de compra parcelada da visão, na ordem do backend (maior compra primeiro). */
export const projectionRowsForView = (
  data: CreditCardProjection,
  view: ProjectionView,
): CreditCardProjectionGroup[] => data.groups.filter(g => groupInView(g, view));

/**
 * "Demais lançamentos" (avulsos + recorrências) da visão, somados por mês.
 * Alinhado a data.months.
 */
export const projectionOthersForView = (data: CreditCardProjection, view: ProjectionView): number[] => {
  const totals = data.months.map(() => 0);
  for (const other of data.others) {
    const included =
      view.kind === 'card'
        ? view.cardId === null || other.idCreditCards === view.cardId
        : view.companyId === null || other.companyId === view.companyId;
    if (!included) continue;
    other.monthAmounts.forEach((v, i) => {
      totals[i] += v;
    });
  }
  return totals;
};

/**
 * Total do mês da visão (parcelas + demais) — a prévia do total da fatura
 * quando a visão é um cartão único.
 */
export const projectionMonthTotals = (data: CreditCardProjection, view: ProjectionView): number[] => {
  const totals = projectionOthersForView(data, view);
  for (const g of projectionRowsForView(data, view)) {
    g.monthAmounts.forEach((v, i) => {
      totals[i] += v;
    });
  }
  return totals;
};

/** A visão tem algum valor (linha ou "demais")? */
export const projectionViewHasData = (data: CreditCardProjection, view: ProjectionView): boolean =>
  projectionRowsForView(data, view).length > 0 ||
  projectionOthersForView(data, view).some(v => v !== 0);
